import React, {useEffect, useState} from 'react';
import {useEnvironment} from '../components/EnvironmentProvider';
import Contact from '../components/Contact';

function ContactForm({formData, setFormData, formType, nextStep}) {

    const env = useEnvironment();
    const applicationId = env.squareAppId;
    const locationId = env.squareLocationId;
    const apiEndpoint = env.apiEndpoint;

    const [isLoading, setIsLoading] = useState(false);
    const [card, setCard] = useState(null);
    const [product, setProduct] = useState(null);  // Initialize as null instead of empty object
    const [paymentOption, setPaymentOption] = useState(null);

    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Effect 1: Initialize product from catalog
    useEffect(() => {
        window.scrollTo(0, 0);

        let foundProduct = {};
        if (formType === 'required') {
            foundProduct = env.productCatalog?.find(p => p.id === "AD-13213");
        } else {
            foundProduct = env.productCatalog?.find(p => p.id === "AD-13214");
        }

        if (foundProduct && foundProduct.billingOptions?.length > 0) {
            setProduct(foundProduct);
            setPaymentOption(foundProduct.billingOptions[0]);
        }
    }, [formType, env.productCatalog]);

    // Effect 2: Load Square card when a paid payment option is ready
    useEffect(() => {
        if (!paymentOption || paymentOption.type === 'no-charge' || formType !== 'required') {
            return;
        }

        let isMounted = true;
        let cardInstance = null;

        const loadSquare = async () => {
            try {
                const payments = window.Square.payments(applicationId, locationId);
                const card = await payments.card();
                cardInstance = card;

                if (isMounted) {
                    await card.attach('#card-container');
                    setCard(card);
                }
            } catch (e) {
                console.error('Square initialization failed', e);
            }
        };

        loadSquare();

        return () => {
            isMounted = false;
            if (cardInstance) {
                cardInstance.destroy();
                setCard(null);
            }
        };
    }, [applicationId, locationId, formType, paymentOption]);

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOptionChange = (event) => {
        const { value, checked } = event.target;

        setFormData((prevData) => ({
            ...prevData,
            releaseInformation: checked
                ? [...(prevData.releaseInformation || []), value] // Add the value if checked
                : (prevData.releaseInformation || []).filter((opt) => opt !== value), // Remove if unchecked
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        // If form validation fails, stop submission
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);

            // Find the first invalid input
            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
            return;
        }

        try {
            setIsLoading(true);

            let result = {};
            if (formType === 'required') {
                if (!card) {
                    throw new Error('Payment form is not ready. Please refresh the page and try again.');
                }
                result = await card.tokenize();
            } else {
                result.status = 'OK';
                result.token = '';
                result.errors = [];
            }

            if (result.status === 'OK') {
                const paymentToken = result.token;
                setErrorMessage(null);
                // Prevent the submit button from being pressed
                setIsLoading(true);

                const submissionData = {
                    memberInfo: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                        emailAddress: formData.emailAddress,
                        phoneNumber: formData.phoneNumber,
                        dateOfBirth: formData.dateOfBirth,
                        gender: formData.gender,
                    },
                    paymentInfo: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        address1: formData.address1,
                        address2: formData.address2,
                        city: formData.city,
                        state: formData.state,
                        zipCode: formData.zipCode,
                        paymentToken: paymentToken
                    },
                    paymentOption: {
                        ...paymentOption,
                        mode: formType,
                    },
                    package: {
                        ...product,
                        verifyTerms: formData.verifyTerms,
                    },
                    release: {
                        providerName: formData.providerName,
                        providerFaxNumber: formData.providerFaxNumber,
                        releaseInformation: formData.releaseInformation
                    },
                    idempotencyKey: formData.idempotencyKey
                };

                delete submissionData.package.billingOptions;

                const response = await fetch(`${apiEndpoint}/release`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify(submissionData),
                    mode: 'cors',
                });

                if (!response.ok) {
                    const errorResponse = await response.json();
                    const msg = errorResponse.message || 'Failed to submit the form';
                    throw new Error(msg);
                }

                setErrorMessage(null);
                nextStep();

            } else {
                throw new Error(result.errors ? result.errors[0].message : 'Tokenization failed');
            }
            setIsLoading(false);
        } catch (e) {
            setErrorMessage(e.message);
            setIsLoading(false);
        }
    };


    // Currency Formatter
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    return (
        <div className="container">
            <div className="row mb-4 align-items-center justify-content-center">
                <div className="col-12 col-md-3 text-center">
                    <img src="/bevital_logo_sm.png" alt="Logo" className="img-fluid"/>
                </div>
                <div className="col-12 col-md-5 text-center text-md-start pt-3 pt-md-0">
                    <h1>Authorization for Release of Medical Information</h1>
                    {/*<h5>One-on-one Wellness Evaluation with Melanie Dorion</h5>*/}
                </div>
            </div>

            <div className="row justify-content-center">
                <div className="col-12 col-md-8">
                    <form noValidate onSubmit={handleSubmit}
                          className={`row g-3 mt-0 ${validated ? 'was-validated' : ''}`}>

                        {/* Contact Form */}
                        <Contact formData={formData} handleChange={handleChange}/>

                        {/* Authoriztion to release */}
                        <div className="col-12 text-start">
                            <div className="mt-3 mb-0"><strong>I HEREBY AUTHORIZE THE RELEASE OF:</strong>
                            </div>


                            <div className="form-check mt-2">
                                <div className='form-check'>
                                    <input
                                        type='checkbox'
                                        id='labatoryResults'
                                        name='releaseInformation'
                                        value='Laboratory Results'
                                        className='form-check-input'
                                        onChange={handleOptionChange}
                                        checked={formData.releaseInformation?.includes('Laboratory Results')}
                                    />
                                    <label className='form-check-label' htmlFor='labatoryResults'>Laboratory
                                        Results</label>
                                </div>
                                <div className='form-check'>
                                    <input
                                        type='checkbox'
                                        id='clinicalNotes'
                                        name='releaseInformation'
                                        value='Clinical Notes'
                                        className='form-check-input'
                                        onChange={handleOptionChange}
                                        checked={formData.releaseInformation?.includes('Clinical Notes')}
                                    />
                                    <label className='form-check-label' htmlFor='clinicalNotes'>Clinical Notes</label>
                                </div>
                            </div>

                            <div className="mt-3 mb-0"><strong>FROM:</strong></div>
                            <div className="form-check mt-2">
                                <address>Melanie Dorion<br/>
                                    Be Vital Health Center<br/>
                                    Phone: (434)-964-6270<br/>
                                    Fax: (434)-234-9843
                                </address>
                            </div>

                            <div className="mt-3 mb-0"><strong>TO:</strong></div>
                            <div className="form-check mt-2">
                                <label htmlFor="providerName" className="form-label">Name of Provider *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="providerName"
                                    name="providerName"
                                    value={formData.providerName}
                                    onChange={handleChange}
                                    required
                                />
                                <div className="invalid-feedback">Name of Provider is required</div>
                            </div>
                            <div className="form-check mt-2">
                                <label htmlFor="providerFaxNumber" className="form-label">Fax Number *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="providerFaxNumber"
                                    name="providerFaxNumber"
                                    value={formData.providerFaxNumber}
                                    onChange={handleChange}
                                    required
                                />
                                <div className="invalid-feedback">Provider Fax Number is required</div>
                            </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className="col-12 text-start">
                            <h4 className="mt-3 mb-0">Terms and Conditions</h4>
                            <div className="collapse mt-2" id="collapseExample">
                                <div className="card card-body py=2"
                                     dangerouslySetInnerHTML={{__html: env.termsConditionsHtml}}/>
                            </div>

                            <div className="form-check mt-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="verifyTerms"
                                    checked={formData.verifyTerms}
                                    id="verifyTerms"
                                    onChange={handleChange}
                                    required
                                />
                                <label htmlFor="verifyTerms" className="form-check-label">
                                    <span>By completing and submitting this form, I hereby authorize disclosure of the
                                        health information for the above named
                                        patient. This authorization is valid for 12 months from the date of signature.
                                        I understand that I may cancel this request with written notification but that
                                        it will not affect any information released prior to notification of
                                        cancellation. I understand that the information used or disclosed may be
                                        subject to re-disclosure by the persons or facility receiving it and would then
                                        no longer be protected by federal regulations. I understand that the medical
                                        provider to whom this authorization is furnished may not condition its treatment
                                        of me on whether or not I sign the authorization.</span>
                                </label>
                                <div className="invalid-feedback">
                                    Please confirm you have read the Terms and Conditions
                                </div>
                            </div>
                        </div>

                        {/* Selected Package Summary */}
                        { product && paymentOption && paymentOption.type !== 'no-charge' && (
                            <div className="card card-body mt-5 bg-light border rounded p-3">
                                <div className="row">
                                    <div className="col-12 col-md-10 text-center text-md-start">
                                        <h5>{product.name}</h5>
                                        <h6 className="text-muted">{paymentOption.name}</h6>
                                        <p>{paymentOption.description}</p>
                                        <p><span className="small text-danger">* Item is not refundable</span></p>
                                    </div>
                                    <div className="col-12 col-md-2 text-md-end text-center text-md-start">
                                        <p className="mb-1"><small>Due Today</small></p>
                                        <h5>{formatter.format(paymentOption.totalAmount)}</h5>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Credit Card Form */}
                        { product && paymentOption && paymentOption.type !== 'no-charge' && (
                        <div className="row mt-5">
                            <div className="col-12">
                                <h4>Payment Details</h4>
                            </div>
                            <div id="card-container" className="col-12"></div>
                            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                        </div>
                        )}

                        {/* Privacy Statement */}
                        <div className="card card-body mt-5">
                            <h5 className="my-3">Privacy Statement</h5>
                            <p>
                                At Be Vital, LLC, we prioritize your privacy and security by adhering to strict
                                HIPAA
                                compliance standards. Your personal information is safeguarded through robust
                                encryption
                                technologies, ensuring that your data is protected both in transit and at rest. We
                                are
                                committed to implementing industry-leading security measures to maintain the
                                confidentiality and integrity of your sensitive information.
                            </p>
                        </div>

                        {/* Submit Button */}
                        <div className="col-12 mt-4 text-center">
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                {isLoading ? 'Processing...' : 'Next'}
                            </button>
                        </div>

                    </form>
                </div>
            </div>

        </div>
    );
}

export default ContactForm;
